import { useCallback, useContext, useEffect, useRef } from 'react'

import { Text } from '@chakra-ui/react'
import { EDITOR_TOOLS, MAXIMUM_NUMBER_OF_SHAPE_DETECTION_CYLINDER } from '@editor/config/constants'
import { INITIAL_SHAPE_STATE } from '@editor/contexts/Editor'
import { GlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import useDetectShapes from '@editor/hooks/useDetectShapes'
import { Editor } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { ShapeKeyType } from '@editor/interfaces/shape'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { detectShapes } from '@editor/services/InspectionArea'
import { meterToMillimeter, withPromiseContained } from '@editor/services/Util'
import { pollAsyncJobStatus } from '@editor/services/VolumeEstimation'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { useToolUtilSelector } from '@editor/store/selectors'
import { addWorkingTool, removeWorkingTool } from '@editor/store/tool'
import { toaster } from '@editor/system/toaster'

import { reset, resetFullIfNoWorkingLabels, resetWorking, setDiameter, setIsLoading } from '../store'

export default function useEditor(props: Editor): EditorConfig {
  // Toast
  const toastIdRef = useRef<string>(undefined)

  // Props
  const { selectedTool, isPreviousTool, changeIsJobRunning, changeTool } = props

  // Context
  const { showErrorModal } = useContext(GlobalModalContext)
  const { getAccessToken } = useContext(UserContext)

  // Store
  const dispatch = useAppDispatch()
  const project = useAppSelector((state) => state.page.project)
  const inspectionArea = useAppSelector((state) => state.page.inspectionArea)
  const inspectionSheet = useAppSelector((state) => state.inspectionItems.inspectionSheet)
  const shapes = useAppSelector((state) => state.shapes.shapes)
  const maskRegions = useAppSelector((state) => state.maskPCD.regions)
  const workingElementId = useToolUtilSelector((state) => state.workingElementId)
  const isSaving = useAppSelector((state) => state.toolRebarDetection.isSaving)
  const workingDistanceLabels = useAppSelector((state) => state.toolRebarDetection.workingLines)
  const baseDiameter = useAppSelector((state) => state.toolRebarDetection.baseDiameter)
  const isDirty = useAppSelector((state) => state.toolRebarDetection.isDirty)

  // Vars
  const isSelectedTool = selectedTool === EDITOR_TOOLS.CYLINDER
  const isSaveable = isDirty && !isSaving && workingDistanceLabels.length > 0 && workingElementId === undefined

  // Hooks
  const { poll } = useDetectShapes(ShapeKeyType.CYLINDER)

  /**
   * Figure out diameter from existing data
   */
  useEffect(() => {
    if (!isSelectedTool || !shapes.cylinders?.length) return

    dispatch(setDiameter(meterToMillimeter(shapes.cylinders[shapes.cylinders.length - 1]?.diameter)))
  }, [isSelectedTool, shapes.cylinders, dispatch])

  /**
   * Start rebar detection
   */
  const handleSave = useCallback(async () => {
    if (inspectionArea?.downsample_status?.status !== FileProcessingStatus.SUCCEEDED || !project || !inspectionSheet) {
      return false
    }

    // If diameter has not been set, highlight the diameter panel and return
    if (!baseDiameter) {
      document.getElementById('property-panel')?.style.setProperty('background-color', 'red')
      setTimeout(() => {
        document.getElementById('property-panel')?.style.removeProperty('background-color')
      }, 550)
      return false
    }

    const token = await getAccessToken()
    if (!token) {
      return false
    }

    dispatch(addWorkingTool(EDITOR_TOOLS.CYLINDER))
    dispatch(setIsLoading(true))
    changeIsJobRunning(true)

    if (toastIdRef.current) toaster.dismiss(String(toastIdRef.current))
    toastIdRef.current = toaster.create({
      title: '鉄筋の検出を開始しました',
      description:
        '鉄筋の検出が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
      type: 'info',
      duration: 5000,
      closable: true,
    })

    return withPromiseContained(async () => {
      const result = await detectShapes(
        token,
        project.project_id,
        inspectionArea.inspection_area_id,
        inspectionSheet.inspection_sheet_id,
        ShapeKeyType.CYLINDER,
        {
          ...INITIAL_SHAPE_STATE(),
          cylinders: workingDistanceLabels.map((label) => ({
            diameter: label.diameter,
            points: label.points,
          })),
        },
        maskRegions.filter((region) => !region.invisible),
        showErrorModal,
      )
      if (!result) return

      const pollResult = await poll(result, 2500, {
        onPostDetect: async (detectResult) => {
          // Re-evaluate volume
          if (detectResult.reevaluate_volume_job_tokens) {
            return Promise.all(
              detectResult.reevaluate_volume_job_tokens.map(async (res) => pollAsyncJobStatus(token, res.job_token)),
            ).then(() => true)
          }

          return true
        },
      })
      if (!pollResult) return

      if (toastIdRef.current) toaster.dismiss(String(toastIdRef.current))
      toastIdRef.current = toaster.create({
        title: '鉄筋検出が完了しました',
        type: 'success',
        duration: 5000,
        closable: true,
      })

      dispatch(resetWorking())
    })
      .catch(() => {
        showErrorModal('鉄筋の検出に失敗しました。時間をおいて再度お試しください。')
      })
      .finally(() => {
        dispatch(setIsLoading(false))
        dispatch(removeWorkingTool(EDITOR_TOOLS.CYLINDER))
        changeIsJobRunning(false)
      })
  }, [
    inspectionArea,
    inspectionSheet,
    project,
    workingDistanceLabels,
    maskRegions,
    baseDiameter,
    getAccessToken,
    showErrorModal,
    poll,
    changeIsJobRunning,
    dispatch,
  ])

  /**
   * Handle apply operation.
   */
  const handleApply = useCallback(async () => {
    dispatch(setAttentionText({ message: '鉄筋を検出中です...' }))
    await handleSave()
    dispatch(setAttentionText({ message: '' }))
  }, [dispatch, handleSave])

  /**
   * Handle submit operation.
   * It's basically save + switch to MOVE/SELECT tool.
   */
  const handleSubmit = useCallback(() => {
    // Switch to MOVE tool
    changeTool(EDITOR_TOOLS.MOVE, true)

    if (isSaveable) {
      if (toastIdRef.current) toaster.dismiss(String(toastIdRef.current))
      toastIdRef.current = toaster.create({
        title: '鉄筋検出を開始しました',
        description:
          '鉄筋検出が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
        type: 'info',
        duration: 5000,
        closable: true,
      })

      void handleSave()
    }
  }, [changeTool, handleSave, isSaveable])

  /**
   * Toggle info panels when tool is selected
   */
  useEffect(() => {
    if (!isSelectedTool) {
      if (isPreviousTool(EDITOR_TOOLS.CYLINDER) && selectedTool !== EDITOR_TOOLS.FOCUS) {
        dispatch(resetFullIfNoWorkingLabels())
      }
    } else if (selectedTool) {
      dispatch(
        setAttentionText({
          message: `鉄筋の始点と終点を選択してください。同時に${MAXIMUM_NUMBER_OF_SHAPE_DETECTION_CYLINDER}個まで指定することができます。\n平面モデルは右側の「検出した要素」パネルの目のアイコンで非表示にすることができます。`,
        }),
      )
    }
  }, [isSelectedTool, isPreviousTool, selectedTool, dispatch])

  /**
   * Reset on unmount
   */
  useEffect(
    () => () => {
      dispatch(reset())
    },
    [dispatch],
  )

  return {
    buttons: {
      apply: {
        key: 'rebar-detection-apply',
        label: `検出`,
        loadingLabel: '検出中',
        tooltip: !baseDiameter ? <Text color="orange.400">直径指定パネルから直径を選択してください</Text> : undefined,
        onClick: handleApply,
        isShown: () => isSelectedTool,
        isLoading: () => isSaving,
        isDisabled: () => !isSaveable,
      },
      submit: {
        key: 'rebar-detection-submit',
        label: isDirty ? '検出して終了' : undefined,
        loadingLabel: '検出中',
        tooltip: !baseDiameter ? <Text color="orange.400">直径指定パネルから直径を選択してください</Text> : undefined,
        isLoading: () => false,
        isShown: () => isSelectedTool,
        isDisabled: () => isDirty && !isSaveable,
        onClick: handleSubmit,
      },
      reset: {
        onClick: () => {
          dispatch(resetWorking())
          document.dispatchEvent(new Event('editor:tool:rebarDetection:reset'))
        },
        isShown: () => isSelectedTool,
        isDisabled: () => isSaving || !isDirty,
      },
      undo: {
        isShown: () => isSelectedTool,
        isDisabled: () => isSaving || !isDirty,
      },
    },
  }
}
